/** @typedef {{ tx?: number, ty?: number, sx?: number, sy?: number, w?: number, h?: number }} ElementTransform */

import { elementLayoutKeyFromElement, resolveElementByKey } from './mediaCreatorDomRegistry';

/** Inline properties owned by text box sizing; removed when the entry no longer has w/h. */
export const TEXT_BOX_PROPS = ['width', 'max-width', 'min-height', 'box-sizing'];
export const TEXT_SIZED_ATTR = 'data-mc-sized';
/** Set on elements whose inline transform came from element_layout, so removed entries can be cleared. */
export const LAYOUT_APPLIED_ATTR = 'data-mc-layout';
export const MIN_TEXT_BOX_W = 24;
export const MIN_TEXT_BOX_H = 16;

export function elementLayoutKey(el) {
  return elementLayoutKeyFromElement(el);
}

/** Text boxes resize like a text area (width/height); they are never scaled. */
export function isTextElement(el) {
  return Boolean(el?.hasAttribute?.('data-editable'));
}

export function transformStyle(transform, { text = false } = {}) {
  if (!transform) return '';
  const tx = Number(transform.tx) || 0;
  const ty = Number(transform.ty) || 0;
  const sx = text ? 1 : Number(transform.sx) || 1;
  const sy = text ? 1 : Number(transform.sy) || 1;
  if (tx === 0 && ty === 0 && sx === 1 && sy === 1) return '';
  if (sx === 1 && sy === 1) return `translate(${tx}px, ${ty}px)`;
  return `translate(${tx}px, ${ty}px) scale(${sx}, ${sy})`;
}

function positiveNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function clearTextBoxSize(el) {
  if (!el?.hasAttribute?.(TEXT_SIZED_ATTR)) return;
  TEXT_BOX_PROPS.forEach((prop) => el.style.removeProperty(prop));
  el.removeAttribute(TEXT_SIZED_ATTR);
}

function applyTextBoxSize(el, t) {
  const w = positiveNumber(t?.w);
  const h = positiveNumber(t?.h);
  if (!w && !h) {
    clearTextBoxSize(el);
    return;
  }
  TEXT_BOX_PROPS.forEach((prop) => el.style.removeProperty(prop));
  if (w) {
    el.style.width = `${w}px`;
    el.style.maxWidth = 'none';
    el.style.boxSizing = 'border-box';
  }
  if (h) el.style.minHeight = `${h}px`;
  el.setAttribute(TEXT_SIZED_ATTR, '');
}

function applyTransform(el, tr) {
  if (tr) {
    el.style.transformOrigin = 'top left';
    el.style.transform = tr;
    el.setAttribute(LAYOUT_APPLIED_ATTR, '');
  } else {
    el.style.removeProperty('transform');
    el.style.removeProperty('transform-origin');
    el.removeAttribute(LAYOUT_APPLIED_ATTR);
  }
}

export function applyElementLayout(root, elementLayout) {
  if (!root) return;
  const entries = elementLayout && typeof elementLayout === 'object' ? elementLayout : {};

  root.querySelectorAll(`[${TEXT_SIZED_ATTR}], [${LAYOUT_APPLIED_ATTR}]`).forEach((el) => {
    const key = elementLayoutKeyFromElement(el);
    if (key && entries[key]) return;
    clearTextBoxSize(el);
    if (el.hasAttribute(LAYOUT_APPLIED_ATTR)) applyTransform(el, '');
  });

  Object.entries(entries).forEach(([key, t]) => {
    const el = resolveElementByKey(root, key);
    if (!el || !t) return;
    const text = isTextElement(el);
    applyTransform(el, transformStyle(t, { text }));
    if (text) applyTextBoxSize(el, t);
  });
}

export const MIN_ELEMENT_SCALE = 0.05;
export const MAX_ELEMENT_SCALE = 50;

export function clampScale(value, min = MIN_ELEMENT_SCALE, max = MAX_ELEMENT_SCALE) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 1;
  return Math.max(min, Math.min(max, n));
}

/**
 * Text box resize from a handle drag (artboard px). The opposite edge stays fixed.
 * @returns {{ tx: number, ty: number, w: number, h: number }}
 */
export function resizeTextBox({ handle, startTx, startTy, startW, startH, dx, dy }) {
  const h = handle || 'se';
  let w = startW;
  let height = startH;
  let tx = startTx;
  let ty = startTy;
  if (h.includes('e')) w = Math.max(MIN_TEXT_BOX_W, startW + dx);
  if (h.includes('w')) {
    w = Math.max(MIN_TEXT_BOX_W, startW - dx);
    tx = startTx + (startW - w);
  }
  if (h.includes('s')) height = Math.max(MIN_TEXT_BOX_H, startH + dy);
  if (h.includes('n')) {
    height = Math.max(MIN_TEXT_BOX_H, startH - dy);
    ty = startTy + (startH - height);
  }
  return { tx, ty, w, h: height };
}

/** Parse inline transform string from element_layout (translate + scale). */
export function parseInlineTransform(transform) {
  const t = (transform || '').trim();
  if (!t || t === 'none') return { tx: 0, ty: 0, sx: 1, sy: 1 };
  const matrix = t.match(/^matrix\(([^)]+)\)$/);
  if (matrix) {
    const p = matrix[1].split(',').map((s) => parseFloat(s.trim()));
    if (p.length >= 6 && p.every((n) => Number.isFinite(n))) {
      return { sx: p[0], sy: p[3], tx: p[4], ty: p[5] };
    }
  }
  let sx = 1;
  let sy = 1;
  let tx = 0;
  let ty = 0;
  const scaleMatch = t.match(/scale\(\s*([^,)]+)\s*(?:,\s*([^)]+))?\s*\)/);
  if (scaleMatch) {
    sx = parseFloat(scaleMatch[1]) || 1;
    sy = parseFloat(scaleMatch[2] ?? scaleMatch[1]) || 1;
  }
  const translateMatch = t.match(/translate\(\s*([^,)]+)\s*(?:,\s*([^)]+))?\s*\)/);
  if (translateMatch) {
    tx = parseFloat(String(translateMatch[1]).replace('px', '')) || 0;
    ty = parseFloat(String(translateMatch[2] ?? '0').replace('px', '')) || 0;
  }
  return { tx, ty, sx, sy };
}
